<template>
  <div id="vehicle-evolution-container" class="bg-white p-2 rounded">
    <!-- Navegação de Período -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <input
          type="text"
          class="form-control"
          placeholder="Filtrar por cliente..."
          v-model="clientNameFilter"
        />
      </div>

      <div class="d-flex align-items-center">
        <button
          class="btn btn-sm btn-primary mr-2"
          type="button"
          @click="exportCSV"
        >
          <i class="fa fa-file-excel fa-lg" />
        </button>

        <Dropdown
          :button-text="selectedViewOption.text"
          :items="viewOptions"
          @item-click="handleViewChange"
          class="mr-2"
        />

        <b-button @click="changePeriod(-1)" :disabled="!canGoBack" pill size="sm">
          <i class="fas fa-chevron-left" />
        </b-button>

        <h5 class="m-0 text-muted ml-2 mr-2">{{ displayedMonths[0] }} à {{ displayedMonths[displayedMonths.length - 1] }} de {{ evolutionData.rows.length }} clientes</h5>

        <b-button @click="changePeriod(1)" :disabled="!canGoForward" pill size="sm">
          <i class="fas fa-chevron-right" />
        </b-button>
      </div>
    </div>

    <!-- Tabela de Dados -->
    <div
      v-if="sortedAndFilteredRows.length"
      class="table-responsive"
      style="max-height: 64vh;"
    >
      <table
        id="vehicle-evolution-table"
        class="table table-striped table-hover"
      >
        <thead>
          <tr>
            <th
              class="sticky-col"
              style="cursor: pointer; min-width: 81px;"
              @click="sortBy('positionAmountAverage')"
            >
              Posição
              <i :class="sortIcon('positionAmountAverage')" class="ml-1"></i>
            </th>

            <th
              class="sticky-col"
              style="min-width: 200px; cursor: pointer;"
              @click="sortBy('client_name')"
            >
              Cliente
              <i :class="sortIcon('client_name')" class="ml-1"></i>
            </th>

            <th
              v-for="month in displayedMonths"
              :key="month"
              class="text-right"
              style="min-width: 130px; cursor: pointer;"
              @click="sortBy(month)"
            >
              <i :class="sortIcon(month)" class="mr-1"></i>
              {{ month }}
            </th>

            <th
              class="text-right"
              style="min-width: 90px; cursor: pointer;"
              @click="sortBy('amountAverage')"
            >
              <i :class="sortIcon('amountAverage')" class="mr-1"></i>
              QTD MÉDIA
            </th>

            <th
              class="text-right"
              style="min-width: 110px; cursor: pointer;"
              @click="sortBy('amountAveragePercentage')"
            >
              <i :class="sortIcon('amountAveragePercentage')" class="mr-1"></i>
              % QTD MÉDIA
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="clientData in sortedAndFilteredRows" :key="clientData.clientId">
            <td class="sticky-col">{{ clientData.positionAmountAverage }}</td>

            <td class="text-limited sticky-col" :title="clientData.client_name">{{ clientData.client_name }}</td>

            <td v-for="month in displayedMonths" :key="month" class="text-right">
              <div v-if="clientData.monthlyData[month]">
                <span v-if="showNumber">
                  {{ clientData.monthlyData[month].amount }}
                </span>

                <span
                  v-if="showEvolution"
                  :class="getBalanceClass(clientData.monthlyData[month].balance)"
                >
                  {{ clientData.monthlyData[month].balance }}

                  <i :class="getBalanceIcon(clientData.monthlyData[month].balance)"></i>
                </span>
              </div>
            </td>

            <td class="text-right">{{ formatCurrency(clientData.amountAverage) }}</td>
            <td class="text-right">{{ formatCurrency(clientData.amountAveragePercentage) }}%</td>
          </tr>
        </tbody>

        <tfoot>
          <tr>
            <td class="sticky-col" colspan="2"><strong>TOTAL</strong></td>

            <td
              class="text-right"
              v-for="month in displayedMonths"
              :key="month"
            >
              <span v-if="showNumber">
                {{ formatCurrency(evolutionData.totalByMonth[month].amount) }}
              </span>

              <span
                v-if="showEvolution"
                :class="getBalanceClass(evolutionData.totalByMonth[month].balance)"
              >
                {{ evolutionData.totalByMonth[month].balance }}

                <i :class="getBalanceIcon(evolutionData.totalByMonth[month].balance)"></i>
              </span>
            </td>

            <td class="text-right">{{ formatCurrency(evolutionData.sumAverageAllClients) }}</td>
            <td class="text-right">{{ formatCurrency(evolutionData.sumAveragePercentageAllClients) }}%</td>
          </tr>

          <tr>
            <td class="sticky-col" colspan="2"><strong>MÉDIA POR CLIENTE</strong></td>

            <td
              class="text-right"
              v-for="month in displayedMonths"
              :key="month"
            >
              <span v-if="showNumber">
                {{ formatCurrency(evolutionData.totalByMonth[month].averageByClientByMonth) }}
              </span>

              <span
                v-if="showEvolution"
                :class="getBalanceClass(evolutionData.totalByMonth[month].averageByClientByMonthBalance)"
              >
                {{ formatCurrency(evolutionData.totalByMonth[month].averageByClientByMonthBalance)}}%

                <i :class="getBalanceIcon(evolutionData.totalByMonth[month].averageByClientByMonthBalance)"></i>
              </span>
            </td>

            <td colspan="100"></td>
          </tr>

          <tr>
            <td class="sticky-col" colspan="2"><strong>INCREMENTO MENSAL</strong></td>

            <td
              class="text-right"
              v-for="month in displayedMonths"
              :key="month"
            >
              <span v-if="showNumber">
                {{ formatCurrency(evolutionData.totalByMonth[month].monthlyIncrement) }}
              </span>

              <span
                v-if="showEvolution"
                :class="getBalanceClass(evolutionData.totalByMonth[month].monthlyIncrementPercentage)"
              >
                {{ formatCurrency(evolutionData.totalByMonth[month].monthlyIncrementPercentage)}}%

                <i :class="getBalanceIcon(evolutionData.totalByMonth[month].monthlyIncrementPercentage)"></i>
              </span>
            </td>

            <td colspan="100"></td>
          </tr>

          <tr>
            <td class="sticky-col" colspan="2"><strong>INCREMENTO ANUAL</strong></td>

            <td
              class="text-right"
              v-for="month in displayedMonths"
              :key="month"
            >
              <span v-if="showNumber">
                {{ formatCurrency(evolutionData.totalByMonth[month].annualIncrement) }}
              </span>

              <span
                v-if="showEvolution"
                :class="getBalanceClass(evolutionData.totalByMonth[month].annualIncrementUp)"
              >
                <i :class="getBalanceIcon(evolutionData.totalByMonth[month].annualIncrementUp)"></i>
              </span>
            </td>

            <td colspan="100"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import Dropdown from '@/components/shared/Dropdown.vue';
import axios from 'axios'
import { apiBaseUrl } from '@/lib/config'

export default {
  name: 'VehicleEvolution',
  components: { Dropdown },
  props: {
    evolutionData: {
      type: Object,
      required: true,
      default: () => {},
    },
  },

  data() {
    return {
      monthsToView: 12,
      currentPeriodEndIndex: 0,
      clientNameFilter: '',
      sortKey: 'positionAmountAverage', // Ordenação inicial por posição
      sortOrder: 'asc', // Ordem ascendente
      viewOptions: [
        { value: 'number_evolution', text: 'Número e Evolução' },
        { value: 'number', text: 'Apenas Número' },
        { value: 'evolution', text: 'Apenas Evolução' },
      ],
      selectedViewOption: {
        value: 'number_evolution',
        text: 'Número e Evolução',
      },
    };
  },

  computed: {
    allMonths() {
      return this.evolutionData && this.evolutionData.months ? this.evolutionData.months : [];
    },

    displayedMonths() {
      if (!this.allMonths.length) return [];
      const endIndex = this.currentPeriodEndIndex + 1;
      const startIndex = Math.max(0, endIndex - this.monthsToView);
      return this.allMonths.slice(startIndex, endIndex);
    },

    canGoBack() {
      // Pode voltar se o índice final do período atual for maior ou igual ao número de meses a exibir
      return this.currentPeriodEndIndex >= this.monthsToView;
    },

    canGoForward() {
      // Pode avançar se o índice final do período atual for menor que o último índice do array de meses
      return this.currentPeriodEndIndex < this.allMonths.length - 1;
    },

    sortedAndFilteredRows() {
      if (!this.evolutionData || !this.evolutionData.rows) {
        return [];
      }

      let rows = [...this.evolutionData.rows];

      // Filtering
      if (this.clientNameFilter) {
        const filter = this.clientNameFilter.toLowerCase();
        rows = rows.filter(client =>
          client.client_name.toLowerCase().includes(filter)
        );
      }

      // Sorting
      if (this.sortKey) {
        const isEvolution = this.selectedViewOption.value === 'evolution';

        rows.sort((a, b) => {
          const isNumber = ['positionAmountAverage', 'amountAverage', 'amountAveragePercentage'].includes(this.sortKey)

          if (isNumber) {
            const valA = a[this.sortKey] || 0;
            const valB = b[this.sortKey] || 0;

            return this.sortOrder === 'asc' ? valA - valB : valB - valA;
          } else if (this.allMonths.includes(this.sortKey)) {
            const month = this.sortKey;

            const valA = a.monthlyData[month] ? a.monthlyData[month][isEvolution ? 'balance' : 'amount'] : -1;
            const valB = b.monthlyData[month] ? b.monthlyData[month][isEvolution ? 'balance' : 'amount'] : -1;

            return this.sortOrder === 'asc' ? valA - valB : valB - valA;
          } else {
            const valA = a[this.sortKey] ? a[this.sortKey].toLowerCase() : '';
            const valB = b[this.sortKey] ? b[this.sortKey].toLowerCase() : '';
            if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;

            return 0;
          }
        });
      }

      return rows;
    },

    totalRows() {
      return this.sortedAndFilteredRows.length;
    },

    showNumber() {
      return ['number_evolution', 'number'].includes(this.selectedViewOption.value);
    },

    showEvolution() {
      return ['number_evolution', 'evolution'].includes(this.selectedViewOption.value)
    }
  },

  methods: {
    changePeriod(direction) {
      const newIndex = this.currentPeriodEndIndex + (direction * this.monthsToView);

      if (direction > 0) {
        this.currentPeriodEndIndex = Math.min(newIndex, this.allMonths.length - 1);
      } else {
        this.currentPeriodEndIndex = Math.max(newIndex, this.monthsToView - 1);
      }
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    sortIcon(key) {
      if (this.sortKey !== key) {
        return 'fa fa-sort text-muted';
      }
      return this.sortOrder === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    handleViewChange(item) {
      this.selectedViewOption = item;
    },
    getBalanceClass(balance) {
      if (balance > 0) return 'badge badge-success w-50'
      if (balance < 0) return 'badge badge-danger w-50'
      return 'badge badge-muted w-50';
    },
    getBalanceIcon(balance) {
      if (balance > 0) return 'mdi mdi-arrow-up-thin';
      if (balance < 0) return 'mdi mdi-arrow-down-thin';
      return 'fa fa-minus';
    },
    formatCurrency (value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "decimal",
        minimumFractionDigits: Number(value) % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    async exportCSV() {
      try {
        const { token, sessionId } = JSON.parse(localStorage.getItem('auth_data'))

        const data = await axios.get(
          `${apiBaseUrl()}vehicle-evolution-excel`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'api-session-id': sessionId
            },
            responseType: 'blob'
          }
        )

        // Cria um link temporário para iniciar o download
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement('a');
        link.href = url;

        // Extrai o nome do arquivo do cabeçalho Content-Disposition
        const contentDisposition = data.headers['content-disposition'];
        let filename = 'evolucao_veiculos_por_cliente.xlsx';

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch.length > 1) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Erro ao exportar o arquivo:', error);
        // Aqui você pode adicionar uma notificação de erro para o usuário
        // Ex: this.$toast.error('Falha ao gerar o relatório.');
      } finally {

      }
    }
  },

  mounted () {
    this.currentPeriodEndIndex = this.allMonths.length - 1;
  },
};
</script>

<style scoped>
#vehicle-evolution-container {
  display: flex;
  flex-direction: column;
  height: 100%; /* Ocupa toda a altura do pai (.ibox-content) */
}

.table-responsive {
  flex-grow: 1; /* Faz a div da tabela ocupar o espaço restante */
  overflow-y: auto; /* Adiciona o scroll vertical quando necessário */
}

.table-responsive thead th {
  position: sticky;
  top: 0;
  background-color: white; /* Garante que o conteúdo que rola por baixo não seja visível */
  z-index: 1; /* Garante que o cabeçalho fique sobre o conteúdo */
  border-bottom-width: 2px; /* Mantém a linha de separação visual */
}

.sticky-col {
  position: -webkit-sticky; /* Para compatibilidade com Safari */
  position: sticky;
  background-color: white;
  z-index: 2;
}

.sticky-col:nth-child(1) {
  left: 0;
}

.sticky-col:nth-child(2) {
  left: 81px; /* Deve ser igual ao min-width da primeira coluna */
}

.table-responsive thead .sticky-col {
  /* Garante que o cabeçalho da coluna fixa fique sobre tudo */
  z-index: 3;
}

.text-limited {
  max-width: 200px;      /* define a largura máxima */
  white-space: nowrap;   /* impede quebra de linha */
  overflow: hidden;      /* esconde o que ultrapassar */
  text-overflow: ellipsis; /* coloca os "..." */
}

tfoot {
  position: sticky;
  bottom: 0;
  background-color: white; /* Garante que o conteúdo que rola por baixo não seja visível */
  z-index: 2; /* Garante que o cabeçalho fique sobre o conteúdo */
}

table {
  border-collapse: separate;
  border-spacing: 0;
}

</style>
